import {useEffect, useState} from 'react'
import {Outlet, useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom'
import {useDialog} from '@/hooks/useDialog'
import {Trash2, Check, AlertTriangle, Plug, Power, PowerOff} from 'lucide-react'
import {useI18n} from '@/i18n'
import {SmallText} from '@/components/shared/SmallText'
import {ErrorText} from '@/components/shared/ErrorText'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {Button} from '@/components/ui/button'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import type {ConnectionTest, PreviewResponse} from '@/types/inbound-connector'
import {PageLayout} from '@/components/shared/PageLayout'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {IconCircle} from '@/components/shared/IconCircle'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {DialogBody} from '@/components/shared/DialogBody'
import {SyncRunsTab} from '@/features/inbound-connectors/components/SyncRunsTab'
import {OverviewSubPage} from '@/features/inbound-connectors/sub-pages/OverviewSubPage'
import {useInboundConnectorPermissions} from '@/features/inbound-connectors/hooks/useInboundConnectorPermissions'
import {useInboundConnectorWithStatus} from '@/features/inbound-connectors/hooks/useInboundConnectorWithStatus'
import {useInboundConnectorMutations} from '@/features/inbound-connectors/hooks/useInboundConnectorMutations'

export const TAB_OVERVIEW = 'overview'
export const TAB_CONFIG = 'configuration'
export const TAB_SYNC_RUNS = 'sync-runs'
export const TAB_PARAM = 'tab'

type TabKey = typeof TAB_OVERVIEW | typeof TAB_CONFIG | typeof TAB_SYNC_RUNS

function resolveTabFromUrl(
    searchTab: string | null,
    pathname: string,
    id: string,
): TabKey {
    if (searchTab === TAB_OVERVIEW || searchTab === TAB_CONFIG || searchTab === TAB_SYNC_RUNS) {
        return searchTab
    }
    if (pathname.startsWith(routes.connectors.inbound.configuration.root(id))) return TAB_CONFIG
    if (pathname.endsWith(`/${TAB_SYNC_RUNS}`)) return TAB_SYNC_RUNS
    return TAB_OVERVIEW
}

export function InboundConnectorDetail() {
    const i18n = useI18n()

    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const {id} = useParams<{id: string}>()
    const {canManage, canExecute} = useInboundConnectorPermissions()

    const {connector, syncStatus, isLoading, error} = useInboundConnectorWithStatus(id)

    const [testResult, setTestResult] = useState<ConnectionTest | null>(null)
    const testResultDialog = useDialog()
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewResponse, setPreviewResponse] = useState<PreviewResponse | null>(null)
    const deleteDialog = useDialog()

    const {enable, disable, triggerSync, triggerDeltaSync, runPreview, testConnection, deleteConnector} =
        useInboundConnectorMutations(id, {
            onTestConnectionSuccess: (result) => {
                setTestResult(result)
                testResultDialog.open()
            },
            onTestConnectionError: () => {
                const failMessage = i18n.inboundConnectors.testConnectionFailed
                setTestResult({success: false, message: {code: 'CONNECTION_TEST_FAILURE', params: {}, message: failMessage, informational: false}, responseTimeMs: null})
                testResultDialog.open()
            },
            onRunPreviewSuccess: (response) => {
                setPreviewResponse(response)
            },
            onRunPreviewError: () => {
                setPreviewResponse({success: false, statusCode: 0, contentType: null, body: null, errorMessage: null})
            },
        })

    const handleToggleEnabled = () => {
        if (!connector) return
        if (connector.enabled) {
            disable.mutate()
        } else {
            enable.mutate()
        }
    }

    const handleTestConnection = () => {
        setTestResult(null)
        testConnection.mutate()
    }

    const handleDelete = () => {
        deleteConnector.mutate(undefined, {
            onSuccess: () => {
                deleteDialog.close()
            },
        })
    }

    const handleRunPreview = () => {
        setPreviewResponse(null)
        setPreviewOpen(true)
        runPreview.mutate()
    }

    const handleClosePreview = () => {
        setPreviewOpen(false)
        setPreviewResponse(null)
    }

    const activeTab: TabKey = id
        ? resolveTabFromUrl(searchParams.get(TAB_PARAM), location.pathname, id)
        : TAB_OVERVIEW

    useEffect(() => {
        if (!id) return
        const wantedTab = searchParams.get(TAB_PARAM)
        if (wantedTab === TAB_CONFIG && !location.pathname.startsWith(routes.connectors.inbound.configuration.root(id))) {
            navigate(routes.connectors.inbound.configuration.connection(id), {replace: true})
        } else if (wantedTab === TAB_SYNC_RUNS && !location.pathname.endsWith(`/${TAB_SYNC_RUNS}`)) {
            navigate(`${routes.connectors.inbound.detail(id)}/${TAB_SYNC_RUNS}`, {replace: true})
        }
    }, [id, searchParams, location.pathname, navigate])

    const handleTabChange = (next: string) => {
        if (!id) return
        if (next === TAB_OVERVIEW) {
            navigate(`${routes.connectors.inbound.detail(id)}?${TAB_PARAM}=${TAB_OVERVIEW}`)
        } else if (next === TAB_CONFIG) {
            navigate(routes.connectors.inbound.configuration.connection(id))
        } else if (next === TAB_SYNC_RUNS) {
            navigate(`${routes.connectors.inbound.detail(id)}/${TAB_SYNC_RUNS}`)
        }
    }

    return (
        <PageLayout
            data-testid="inbound-connector-detail-page"
            variant="detail"
            title={connector?.displayName ?? i18n.inboundConnectors.detailPageTitle}
            icon={CONCEPT_ICON.connectorInbound}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(connector, 'INBOUND_CONNECTOR') : undefined)}
            backTo={routes.connectors.inbound.list()}
            actions={connector && (canExecute || canManage) ? (
                <InlineRow>
                    {canExecute && (
                        <Button
                            data-testid="btn-test-connection"
                            variant="outline"
                            onClick={handleTestConnection}
                            disabled={testConnection.isPending}
                        >
                            {testConnection.isPending ? (
                                <ButtonSpinner/>
                            ) : (
                                <Icon icon={Plug} className="mr-2"/>
                            )}
                            {i18n.inboundConnectors.testConnection}
                        </Button>
                    )}
                    {canManage && (
                        <Button
                            data-testid="btn-toggle-enabled"
                            variant={connector.enabled ? 'outline' : 'default'}
                            onClick={handleToggleEnabled}
                            disabled={enable.isPending || disable.isPending}
                        >
                            {connector.enabled ? (
                                <>
                                    <Icon icon={PowerOff} className="mr-2"/>
                                    {i18n.inboundConnectors.disable}
                                </>
                            ) : (
                                <>
                                    <Icon icon={Power} className="mr-2"/>
                                    {i18n.inboundConnectors.enable}
                                </>
                            )}
                        </Button>
                    )}
                    {canManage && (
                        <Button
                            data-testid="btn-delete"
                            variant="destructive"
                            onClick={deleteDialog.open}
                        >
                            <Icon icon={Trash2} className="mr-2"/>
                            {i18n.inboundConnectors.deleteConnector}
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {connector && <>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
                <TabsList data-testid="detail-tabs">
                    <TabsTrigger value={TAB_OVERVIEW} data-testid="tab-overview">
                        {i18n.inboundConnectors.tabOverview}
                    </TabsTrigger>
                    <TabsTrigger value={TAB_CONFIG} data-testid="tab-configuration">
                        {i18n.inboundConnectors.tabConfiguration}
                    </TabsTrigger>
                    <TabsTrigger value={TAB_SYNC_RUNS} data-testid="tab-sync-runs">
                        {i18n.inboundConnectors.tabSyncRuns}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value={TAB_OVERVIEW}>
                    <OverviewSubPage
                        connector={connector}
                        syncStatus={syncStatus}
                        canExecute={canExecute}
                        triggerMutation={triggerSync}
                        deltaSyncMutation={triggerDeltaSync}
                        previewMutation={runPreview}
                        onTriggerSync={(syncType) => triggerSync.mutate(syncType)}
                        onTriggerDeltaSync={() => triggerDeltaSync.mutate()}
                        onRunPreview={handleRunPreview}
                        onPreviewClose={handleClosePreview}
                        previewOpen={previewOpen}
                        previewResponse={previewResponse}
                        lastSyncCreatedCountTestId="last-sync-created-count"
                        lastSyncUpdatedCountTestId="last-sync-updated-count"
                        lastSyncErroredCountTestId="last-sync-errored-count"
                    />
                </TabsContent>

                <TabsContent value={TAB_CONFIG}>
                    <Stack className="mt-4">
                        <Outlet/>
                    </Stack>
                </TabsContent>

                <TabsContent value={TAB_SYNC_RUNS}>
                    <SyncRunsTab connectorId={connector.id}/>
                </TabsContent>
            </Tabs>

            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent data-testid="delete-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.inboundConnectors.deleteConnector}</DialogTitle>
                        <DialogDescription>{i18n.inboundConnectors.deleteConfirmation}</DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete"
                        onCancel={deleteDialog.close}
                        saving={deleteConnector.isPending}
                        saveLabel={i18n.common.delete}
                        destructive
                        saveType="button"
                        onSave={handleDelete}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={testResultDialog.isOpen} onOpenChange={testResultDialog.bind.onOpenChange}>
                <DialogContent data-testid="test-result-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.inboundConnectors.testConnection}</DialogTitle>
                    </DialogHeader>
                    {testResult && (
                        <DialogBody>
                            {testResult.success ? (
                                <InlineRow gap="lg">
                                    <IconCircle icon={Check} variant="success"/>
                                    <Stack gap="xs">
                                        <MutedText>{i18n.inboundConnectors.testConnectionSuccess}</MutedText>
                                        {testResult.responseTimeMs !== null && (
                                            <SmallText>{i18n.inboundConnectors.testConnectionTime({ms: String(testResult.responseTimeMs)})}</SmallText>
                                        )}
                                    </Stack>
                                </InlineRow>
                            ) : (
                                <InlineRow gap="lg">
                                    <IconCircle icon={AlertTriangle} variant="destructive"/>
                                    <ErrorText>{testResult.message?.message ?? i18n.inboundConnectors.testConnectionFailed}</ErrorText>
                                </InlineRow>
                            )}
                        </DialogBody>
                    )}
                    <SaveCancelFooter
                        testIdPrefix="test-result"
                        onCancel={testResultDialog.close}
                        saveLabel={i18n.common.close}
                        saveType="button"
                        onSave={testResultDialog.close}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
